/**
 * Persistência dedicada ao modo mock para refresh tokens válidos (sobrevive a F5 na mesma origem).
 * Logout revoga entrada correspondente; tokens inválidos continuam retornando 401 nos handlers.
 */

const MOCK_REFRESH_STORAGE_KEY = 'imo.mock.validRefreshTokens';

function browserStorages(): Storage[] {
  if (typeof globalThis.sessionStorage === 'undefined' || typeof globalThis.localStorage === 'undefined')
    return [];

  return [globalThis.sessionStorage, globalThis.localStorage];
}

function readMergedRefreshTokensFromBrowserStorage(): Set<string> {
  const merged = new Set<string>();

  for (const storage of browserStorages()) {
    try {
      const raw = storage.getItem(MOCK_REFRESH_STORAGE_KEY);

      if (raw == null)
        continue;

      const parsed = JSON.parse(raw) as unknown;

      if (!Array.isArray(parsed))
        continue;

      for (const entry of parsed) {
        if (typeof entry === 'string')
          merged.add(entry);
      }
    } catch {
      /* ignore malformed JSON */
    }
  }

  return merged;
}

function writeRefreshTokensToBrowserStorage(tokens: Set<string>): void {
  const storages = browserStorages();

  if (storages.length === 0)
    return;

  const serialized = JSON.stringify([...tokens]);

  for (const storage of storages) {
    try {
      storage.setItem(MOCK_REFRESH_STORAGE_KEY, serialized);
    } catch {
      /* quota / modo restrito */
    }
  }
}

const activeMockRefreshTokens = new Set<string>(readMergedRefreshTokensFromBrowserStorage());

/** Simula novo bundle mantendo storages — apenas para testes. */
export function clearMockAuthRefreshTokenMemoryOnlyForTests(): void {
  activeMockRefreshTokens.clear();
}

export function hydrateMockAuthRefreshTokensFromBrowserStorage(): void {
  activeMockRefreshTokens.clear();

  for (const token of readMergedRefreshTokensFromBrowserStorage())
    activeMockRefreshTokens.add(token);
}

export function mockAuthRefreshTokenActive(token: string): boolean {
  return activeMockRefreshTokens.has(token);
}

export function mockAuthRefreshTokenRegister(token: string): void {
  activeMockRefreshTokens.add(token);
  writeRefreshTokensToBrowserStorage(activeMockRefreshTokens);
}

export function mockAuthRefreshTokenRevoke(token: string): void {
  activeMockRefreshTokens.delete(token);
  writeRefreshTokensToBrowserStorage(activeMockRefreshTokens);
}

export function mockAuthRefreshTokenRotate(oldToken: string, newToken: string): void {
  activeMockRefreshTokens.delete(oldToken);
  activeMockRefreshTokens.add(newToken);
  writeRefreshTokensToBrowserStorage(activeMockRefreshTokens);
}

/** Memória + storages — usar em beforeEach de testes Vitest que tocam login mock. */
export function resetMockAuthRefreshTokenStoreForTests(): void {
  activeMockRefreshTokens.clear();

  for (const storage of browserStorages()) {
    try {
      storage.removeItem(MOCK_REFRESH_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}
